/**
 * Motion physics for the Varzishmate marketing site.
 *
 * The constants come from Apple's "Designing Fluid Interfaces" (WWDC 2018).
 * Apple parameterises springs by damping ratio and response rather than
 * mass/stiffness/damping; framer-motion's `bounce` and `duration` map onto
 * that pair directly.
 *
 * These functions are pure and DOM-free on purpose. jsdom reports zero for
 * every layout measurement and does not implement setPointerCapture, so
 * physics that lives inside a component cannot be tested. Physics that lives
 * here can.
 */

/**
 * Damping 1.0 means a graceful settle with no overshoot. Anything that merely
 * appears on screen uses that. Bounce is reserved for motion a gesture handed
 * velocity to — overshoot on a flicked card reads as physical, overshoot on a
 * menu that just faded in reads as a bug.
 */
export const SPRING = {
  ui: { type: 'spring', bounce: 0, duration: 0.35 },
  move: { type: 'spring', bounce: 0, duration: 0.4 },
  momentum: { type: 'spring', bounce: 0.2, duration: 0.4 },
  sheet: { type: 'spring', bounce: 0.15, duration: 0.3 },
};

/**
 * Where a flick would come to rest, given its release velocity.
 *
 * This is the exponential-decay form Apple ships, not the textbook
 * v^2 / (2 * deceleration). Use it to choose the snap target, so a flick
 * throws the content instead of falling back to whatever was nearest at the
 * moment the finger lifted.
 *
 * @param {number} velocity px/s at release
 * @param {number} [decelerationRate] 0.998 is normal scroll feel; 0.99 is snappier
 * @returns {number} signed distance still to travel, in px
 */
export function project(velocity, decelerationRate = 0.998) {
  return ((velocity / 1000) * decelerationRate) / (1 - decelerationRate);
}

/**
 * Progressive resistance past a boundary. A hard stop reads as frozen; this
 * reads as "still responding, but there is nothing more here".
 *
 * @param {number} overshoot how far past the bound the pointer has travelled
 * @param {number} dimension the size of the scrollable area, used as the scale
 * @param {number} [constant] higher is looser
 * @returns {number} the displacement actually applied, always < overshoot
 */
export function rubberband(overshoot, dimension, constant = 0.55) {
  return (overshoot * dimension * constant) / (dimension + constant * Math.abs(overshoot));
}

/**
 * Clamp to [min, max], but rubber-band rather than truncate outside it.
 */
export function clampWithRubberband(x, min, max, dimension, constant = 0.55) {
  if (x > max) return max + rubberband(x - max, dimension, constant);
  if (x < min) return min - rubberband(min - x, dimension, constant);
  return x;
}

/**
 * Which card a release should land on: project the momentum forward first,
 * then snap to whatever is nearest the *projected* point.
 *
 * @param {object} args
 * @param {number} args.offset current track x, negative as it advances
 * @param {number} args.velocity release velocity in px/s
 * @param {number} args.step card width plus gap
 * @param {number} args.count number of cards
 * @param {number} [args.decelerationRate]
 * @returns {number} a valid index in [0, count - 1]
 */
export function snapIndexFor({ offset, velocity, step, count, decelerationRate = 0.998 }) {
  const projected = offset + project(velocity, decelerationRate);
  const raw = Math.round(-projected / step);
  return Math.min(count - 1, Math.max(0, raw));
}
